import { useMeasurement } from "lib/@hooks/useMeasurement"
import { Bound } from "lib/@components/binding/Bound"
import { idNotEqual } from "lib/idEquals"
import { TwoSchedules } from "routes/schedule/compare/twoSchedules"
import { checkTaskEquality } from "routes/schedule/compare/checkTaskEquality"
import { compareTwoObjects } from "routes/schedule/compare/task-header-bar"
import { Box } from "@mui/material"
import { TaskOrHeader } from "routes/schedule/tasks/taskOrHeader"
import { copyTaskContent } from "routes/schedule/compare/copyTaskContent"
import { ComparisonUpdated } from "event-definitions"
import { cloneDeep } from "@apollo/client/utilities"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { findTask } from "routes/schedule/compare/find-task"
import { InlineCompareButtons } from "./inline-approval-buttons/inline-compare-buttons"

export function CompareTasks({ task }) {
    const [{ width, height }, attach] = useMeasurement()
    const { comparisonTarget, compare, schedule, target } = useBoundContext()
    const otherTask = findTask(comparisonTarget.tasks, task)
    const originalTask = findTask(compare.tasks, task)
    const editedTask = findTask(target.tasks, task)
    return (
        <Bound acceptId={task.title}>
            <TwoSchedules
                key={task.id}
                copy={copyTask(editedTask, otherTask)}
                copied={
                    !editedTask ||
                    (checkTaskEquality(editedTask, otherTask) && !checkTaskEquality(originalTask, otherTask))
                }
                revert={revertTask(editedTask, otherTask)}
                showCopy={!checkTaskEquality(originalTask, otherTask)}
                leftSchedule={target}
                disabled={!editedTask}
                rightSchedule={comparisonTarget}
                left={
                    <Bound
                        comparisonFunction={compareTwoObjects(task, otherTask ?? task, otherTask ?? task)}
                        target={editedTask}
                        originalTarget={otherTask || task}
                    >
                        <Box ref={attach} width={1}>
                            {!editedTask ? (
                                <Box
                                    alignItems="center"
                                    width={width}
                                    height={110}
                                    bgcolor="action.selected"
                                    display="flex"
                                    justifyContent="center"
                                >
                                    Removed &quot;{task.title}&quot; from schedule &quot;{schedule.title}&quot;
                                </Box>
                            ) : (
                                <TaskOrHeader last={true} item={editedTask} />
                            )}
                        </Box>
                    </Bound>
                }
                right={
                    <Bound
                        originalTarget={task}
                        comparisonFunction={compareTwoObjects(task, otherTask ?? task, otherTask ?? task)}
                    >
                        {otherTask && <TaskOrHeader last={true} item={otherTask} />}
                        {!otherTask && (
                            <Box py={1}>
                                <Box
                                    borderRadius={1}
                                    width={width}
                                    minWidth={180}
                                    p={1}
                                    height={height}
                                    minHeight={64}
                                    textAlign="center"
                                    alignItems="center"
                                    bgcolor="action.selected"
                                    display="flex"
                                    justifyContent="center"
                                >
                                    Not found in this version
                                </Box>
                            </Box>
                        )}
                    </Bound>
                }
                after={<InlineCompareButtons idFromGroup={task.title} to={otherTask} from={originalTask} />}
            />
        </Bound>
    )

    function copyTask(task, otherTask) {
        return () => {
            if (!otherTask) {
                target.tasks = target.tasks.filter(idNotEqual(task.id))
            } else {
                copyTaskContent(task, otherTask)
            }
            ComparisonUpdated.raiseOnce()
        }
    }

    function revertTask(task, otherTask) {
        return () => {
            if (!otherTask) {
                target.tasks.push(cloneDeep(task))
            } else {
                const originalTask = compare.tasks.find((t) => t.id === task.id)
                copyTaskContent(task, originalTask)
            }
            ComparisonUpdated.raiseOnce()
        }
    }
}
